import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
} from '@roger/ui-components';
import { AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';

interface ResetPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  username: string;
  displayName: string;
}

export const ResetPasswordDialog: React.FC<ResetPasswordDialogProps> = ({
  open,
  onOpenChange,
  username,
  displayName,
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(true);

  const { resetPassword, showSuccess, showError } = useStore(
    useShallow(state => ({
      resetPassword: state.resetPassword,
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      showError('Please enter and confirm the new password');
      return;
    }

    if (newPassword !== confirmPassword) {
      showError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      showError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(username, newPassword);
      showSuccess(`Password reset successfully for ${displayName}`);
      setNewPassword('');
      setConfirmPassword('');
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to reset password:', error);
      showError('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset Password for {displayName}</DialogTitle>
        </DialogHeader>
        <div className='py-2 text-sm text-muted-foreground'>
          <strong>Confirm:</strong> Are you sure you want to generate a new
          password for {username}? The old one will no longer work.
        </div>
        <form onSubmit={handleSubmit}>
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <label htmlFor='newPassword' className='text-sm font-medium'>
                  New Password
                </label>
                <button
                  type='button'
                  onClick={() => setShowPassword(!showPassword)}
                  className='text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1'
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <>
                      <EyeOff className='h-4 w-4' />
                      <span>Hide</span>
                    </>
                  ) : (
                    <>
                      <Eye className='h-4 w-4' />
                      <span>Show</span>
                    </>
                  )}
                </button>
              </div>
              <Input
                id='newPassword'
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder='Enter new password'
                required
              />
            </div>
            <div className='space-y-2'>
              <label htmlFor='confirmPassword' className='text-sm font-medium'>
                Confirm Password
              </label>
              <Input
                id='confirmPassword'
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder='Confirm new password'
                required
              />
            </div>
            <div
              role='alert'
              className='flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3'
            >
              <AlertTriangle className='h-4 w-4 shrink-0 text-amber-600' />
              <p className='text-sm text-amber-900'>
                <strong>Please copy the updated credentials.</strong>
                <br />A notification email will <strong>NOT</strong> be sent to
                the user. You must contact the user and send their updated
                credentials directly.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type='submit' disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
